import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

/**
 * SeededListingNotice — Iter 128 (Feb 2026)
 *
 * Soft amber notice shown on every listing that hasn't been signed up for
 * by a real owner ("seeded" or "claimed" ownershipStatus). Hidden once the
 * listing flips to "owner_verified".
 *
 * Legal goal: make it unambiguous that the business shown on the page has
 * not (yet) confirmed or endorsed the listing — eliminating any implied
 * partnership, verification, or owner approval. Owners get two clear CTAs
 * (claim or request edit/removal) right under the notice.
 */
const SeededListingNotice = ({ ownershipStatus, source, isClaimed, userId, onClaim, onRequestRemoval, businessName }) => {
  const { t } = useTranslation();

  // Iter 162 — Hard safety guard. Never render this notice for claimed,
  // owner-verified, or owner/founder-managed listings. The notice is reserved
  // exclusively for genuinely unclaimed seeded listings.
  const isClaimedOrOwned =
    ownershipStatus === 'claimed' ||
    ownershipStatus === 'owner_verified' ||
    isClaimed === true ||
    source === 'owner' ||
    source === 'founders' ||
    !!userId;
  if (isClaimedOrOwned) return null;

  const isSeededUnclaimed =
    source === 'seed' ||
    source === 'seeded' ||
    ownershipStatus === 'seeded' ||
    ownershipStatus === 'unclaimed';
  if (!isSeededUnclaimed) return null;

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-start"
      data-testid="seeded-listing-notice"
    >
      <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-amber-900 leading-relaxed">
          {t('seededNotice.body',
            'This listing was created using publicly available business information to help local users discover businesses in the area. Business owners can claim, edit, or request removal of the listing at any time.')}
        </p>
        <p className="text-xs text-amber-800/80 mt-1 italic">
          {t('seededNotice.disclaimer',
            'Information shown may come from publicly available sources.')}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {onClaim && (
            <button
              onClick={onClaim}
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
              data-testid="seeded-claim-btn"
            >
              {t('seededNotice.claimCta', 'Claim this listing')}
            </button>
          )}
          {onRequestRemoval && (
            <button
              onClick={onRequestRemoval}
              className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-900 text-xs font-semibold hover:bg-amber-100"
              data-testid="seeded-removal-btn"
            >
              {t('seededNotice.removalCta', 'Request edit or removal')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeededListingNotice;
